import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  DialogSyncPaymentDateData,
  DialogSyncPaymentDateResult,
  MassOsCurrency,
  MassOsGenerationMode,
  MassOsItineraryPaymentState,
  MassOsPaymentDateStatus,
  MassOsServicePaymentState,
  MassOsTicketPaymentState
} from 'src/entities/file';
import {
  CreateOrderInput,
  CreateOrderItemInput,
  CreateOrderResult,
  CreatedOrder,
  OrderService,
  RejectedService,
  formatUserMessageText,
  logOrderDevDetail,
  resolveCreateOrderRejectionMessage,
  resolveCreateOrderWarningMessage,
  resolveOrderGraphqlUserMessage,
  toCreateOrderResult,
  toScheduledPaymentDateIso
} from 'src/entities/order';
import {
  CreatePurchaseNoteInput,
  CreatePurchaseNoteResult,
  CreatedPurchaseNote,
  PurchaseNoteService,
  toCreatePurchaseNoteResult
} from 'src/entities/purchase-note';
import {
  resolveCreatePurchaseNoteRejectionMessage,
  resolveCreatePurchaseNoteWarningMessage,
  resolvePurchaseNoteGraphqlUserMessage
} from 'src/entities/purchase-note/lib/purchase-note-user-messages';

export interface OsUnitState {
  kind: 'service' | 'ticket';
  serviceId: string;
  ticketId?: string;
  moneda: MassOsCurrency;
  total: number;
  nombreServicio: string;
  nombre: string;
  serviceDay: number;
  fecha: Date;
  paymentDate: Date;
  codigoReserva: string | null;
}

export interface OsCodigoReservaGroup {
  fileNumber: string;
  moneda: MassOsCurrency;
  codigoReserva: string;
  fechaPago: Date;
  services: MassOsServicePaymentState[];
  units: OsUnitState[];
  total: number;
  observaciones: string;
  /** Presentes solo si el lote incluye comisión. `total` sigue siendo el monto base. */
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  payableTotal?: number | null;
  commissionLabel?: string | null;
  /** Nº secuencial de OS dentro del resumen (para el badge legible). */
  seq?: number;
}

export interface OsMonedaGroup {
  moneda: MassOsCurrency;
  codigoGrupos: OsCodigoReservaGroup[];
}

export interface OsFechaPagoGroup {
  fechaPago: Date;
  monedaGrupos: OsMonedaGroup[];
}

export interface OsFileGroup {
  fileNumber: string;
  fechaGrupos: OsFechaPagoGroup[];
}

/** Textos parametrizados del dialog según el modo OS/NP. */
export interface Labels {
  documentTitle: string;
  documentSubtitle: string;
  step1Label: string;
  step1ProviderDesc: string;
  step1Info: string;
  step2Info?: string;
  step3Info: string;
  step3Empty: string;
  fileCountLabel: string;
  fechaGroupLabel: string;
  unitLabel: string;
  statUnitLabel: string;
  confirmTitle: string;
  confirmSubtitle: string;
  generatingTitle: string;
  generatingSubtitle: string;
  generationButton: string;
  resultSuccessTitle: string;
  resultSuccessSubtitle: string;
  resultWarnTitle: string;
  resultWarnSubtitle: string;
  resultErrorTitle: string;
  resultErrorSubtitle: string;
  createdResultLabel: string;
  createdResultNumberLabel: string;
  createdResultStatus: string;
}

@Component({
  selector: 'app-dialog-sync-payment-date',
  templateUrl: './dialog-sync-payment-date.component.html',
  styleUrls: ['./dialog-sync-payment-date.component.css']
})
export class DialogSyncPaymentDateComponent implements OnDestroy {
  readonly steps: string[];
  activeStep = 0;

  /** Modo del wizard (OS o NP). */
  readonly mode: MassOsGenerationMode;

  readonly itineraryStates: MassOsItineraryPaymentState[];

  /** Estado del flujo de creación dentro del dialog. */
  viewState: 'idle' | 'generating' | 'result' | 'error' = 'idle';

  /** true cuando se muestra la vista de confirmación antes de generar (paso de doble confirmación). */
  confirming = false;

  private result: CreateOrderResult | null = null;
  private results:
    | { kind: 'os'; value: CreateOrderResult }
    | { kind: 'np'; value: CreatePurchaseNoteResult }
    | null = null;
  private createdCountInternal = 0;
  private errorTitle: string | null = null;
  private errorMessage: string | null = null;

  private subscriptions: Subscription[] = [];
  private lastInputs: CreateOrderInput[] = [];
  private lastPurchaseNoteInputs: CreatePurchaseNoteInput[] = [];

  constructor(
    private readonly dialogRef: MatDialogRef<
      DialogSyncPaymentDateComponent,
      DialogSyncPaymentDateResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: DialogSyncPaymentDateData,
    private readonly orderService: OrderService,
    private readonly purchaseNoteService: PurchaseNoteService
  ) {
    this.mode = data.mode ?? 'os';
    this.steps =
      this.mode === 'np'
        ? ['Fecha de Revisión', 'Comisión', 'Resumen']
        : ['Fecha de Pago', 'Código de Reserva', 'Emisión y comisión', 'Resumen'];
    this.itineraryStates = this.buildInitialState();
    this.buildOsGroups();
    const freezeDeadline = this.defaultBatchFreezeDeadline();
    if (freezeDeadline) {
      this.applyFreezeDeadline(freezeDeadline);
    }
  }

  /**
   * Plazo para confirmar el lote ya calculado: 45 días antes de la
   * fecha de operación más cercana entre servicios y tickets del lote,
   * pero nunca una fecha que ya pasó: si ese día ya pasó, es hoy.
   */
  private defaultBatchFreezeDeadline(): Date | null {
    const times: number[] = [];
    for (const it of this.itineraryStates ?? []) {
      for (const svc of it.serviceStates ?? []) {
        const svcTime = svc.fecha ? new Date(svc.fecha).getTime() : NaN;
        if (!Number.isNaN(svcTime)) {
          times.push(svcTime);
        }
        for (const ticket of svc.tickets ?? []) {
          const ticketTime = ticket.fecha
            ? new Date(ticket.fecha).getTime()
            : NaN;
          if (!Number.isNaN(ticketTime)) {
            times.push(ticketTime);
          }
        }
      }
    }
    if (!times.length) {
      return null;
    }
    const earliest = new Date(Math.min(...times));
    earliest.setDate(earliest.getDate() - 45);
    earliest.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return earliest.getTime() < today.getTime() ? today : earliest;
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  get providerName(): string {
    return this.data.provider?.name ?? '';
  }

  get condicionPagoDias(): number {
    if (!this.data.provider) {
      return 0;
    }
    for (const itinerary of this.data.itineraries) {
      for (const svc of itinerary.servicios) {
        if (
          (svc.providerId ?? null) !== (this.data.provider.id ?? null) ||
          !svc.providerCondicionesPagos
        ) {
          continue;
        }
        const cond = svc.providerCondicionesPagos;
        const condicionPago =
          String(cond.condicionPago ?? '').toUpperCase() === 'PREPAGO'
            ? 'PREPAGO'
            : 'POSTPAGO';
        const plazoDiasFit = cond.plazoDiasFit;
        return plazoDiasFit != null && !Number.isNaN(Number(plazoDiasFit))
          ? Number(plazoDiasFit)
          : condicionPago === 'PREPAGO'
            ? 30
            : 0;
      }
    }
    return 0;
  }

  get isLastStep(): boolean {
    return this.activeStep === this.steps.length - 1;
  }

  get isOsMode(): boolean {
    return this.mode !== 'np';
  }

  get isNpMode(): boolean {
    return this.mode === 'np';
  }

  /** true en modo NP cuando el dialog está en el paso de resumen (último paso) o en el flujo de creación (confirm/generando/resultado/error). */
  get isNpStep3Mode(): boolean {
    if (this.mode !== 'np') {
      return false;
    }
    if (this.viewState !== 'idle') {
      return true;
    }
    return this.activeStep === this.steps.length - 1 || this.confirming;
  }

  /** Clase del panel del datepicker: se mantiene el estilo base (naranja). */
  get datepickerPanelClass(): string {
    return 'sync-datepicker-panel';
  }

  /** Labels del documento según el modo (OS ↔ NP). */
  get labels(): Labels {
    return this.mode === 'np'
      ? {
          documentTitle: 'Generar Notas de Pedido',
          documentSubtitle:
            'Configura la fecha de revisión y, si aplica, la comisión antes de emitir las NP.',
          step1Label: 'Fecha de Revisión',
          step1ProviderDesc:
            'FECHA DE REVISIÓN: <strong>' + (this.providerName || '') + '</strong>' +
            ', ' + this.condicionPagoDias + ' días de anticipo',
          step1Info:
            'La fecha de revisión de las notas de pedido se calcula automáticamente según los términos de pago del proveedor. Puedes ajustarla de manera masiva para todos los servicios del file. Recuerda que la fecha de revisión individual siempre tendrá prioridad.',
          step3Info: 'Revisa el resumen antes de generar las Notas de Pedido.',
          step3Empty:
            'No hay servicios ni notas de pedido para generar. Revisa los pasos anteriores o ajusta tu selección.',
          fileCountLabel: 'fecha(s) de revisión',
          fechaGroupLabel: 'Revisión:',
          unitLabel: 'NP',
          statUnitLabel: 'Notas',
          confirmTitle: '¿Confirmar la generación de {{0}} notas de pedido?',
          confirmSubtitle:
            'Se emitirán {{0}} NP para {{1}} servicio(s) del proveedor {{2}}. Esta acción es irreversible.',
          generatingTitle: 'Generando notas de pedido…',
          generatingSubtitle:
            'Esto puede tardar unos segundos mientras se numeran y confirman los servicios.',
          generationButton: 'Generar Notas de Pedido',
          resultSuccessTitle: 'Notas de pedido creadas correctamente',
          resultSuccessSubtitle: '{{0}} NP generada(s)',
          resultWarnTitle: 'NP creada con observaciones',
          resultWarnSubtitle: '{{0}} NP generada(s). Revisa las advertencias.',
          resultErrorTitle: 'No se creó ninguna NP',
          resultErrorSubtitle: 'Revisa el detalle de lo no procesado.',
          createdResultLabel: 'Notas de pedido creadas',
          createdResultNumberLabel: 'Número NP',
          createdResultStatus: ''
        }
      : {
          documentTitle: 'Generar Órdenes de Servicio',
          documentSubtitle:
            'Configura la fecha de pago, el código de reserva, cómo nace la OS y, si aplica, la comisión antes de emitir las OS.',
          step1Label: 'Fecha de Pago',
          step1ProviderDesc:
            'FECHA DE PAGO: <strong>' + (this.providerName || '') + '</strong>' +
            ', ' + this.condicionPagoDias + ' días de anticipo',
          step1Info:
            'Las fechas de pago programada se calculan automáticamente según los términos de pago del proveedor. Puedes ajustar de manera masiva para todos los servicios del file, día por día. Recuerda que las fechas individuales siempre tendrán prioridad.',
          step2Info:
            'Asocia códigos de reserva a los servicios seleccionados. Puedes ingresar un código de manera masiva para todo el file o servicio por servicio. El código de reserva es opcional.',
          step3Info: 'Revisa el resumen antes de generar las Órdenes de Servicio.',
          step3Empty:
            'No hay servicios ni órdenes de servicio para generar. Revisa los pasos anteriores o ajusta tu selección.',
          fileCountLabel: 'fecha(s) de pago',
          fechaGroupLabel: 'Pago programado:',
          unitLabel: 'OS',
          statUnitLabel: 'Órdenes',
          confirmTitle: '¿Confirmar la generación de {{0}} órdenes de servicio?',
          confirmSubtitle:
            'Se emitirán {{0}} OS para {{1}} servicio(s) del proveedor {{2}}. Esta acción es irreversible.',
          generatingTitle: 'Generando órdenes de servicio…',
          generatingSubtitle:
            'Esto puede tardar unos segundos mientras se numeran y confirman los servicios.',
          generationButton: 'Generar Órdenes de Servicio',
          resultSuccessTitle: 'Órdenes creadas correctamente',
          resultSuccessSubtitle: '{{0}} OS generada(s)',
          resultWarnTitle: 'OS creada con observaciones',
          resultWarnSubtitle: '{{0}} OS generada(s). Revisa las advertencias.',
          resultErrorTitle: 'No se creó ninguna OS',
          resultErrorSubtitle: 'Revisa el detalle de lo no procesado.',
          createdResultLabel: 'Órdenes creadas',
          createdResultNumberLabel: 'Número OS',
          createdResultStatus: 'os'
        };
  }

  get totalServices(): number {
    return this.itineraryStates.reduce(
      (sum, it) => sum + it.serviceStates.length,
      0
    );
  }

  /** Estado estable de la agrupación File → Fecha → Moneda → Código de Reserva. */
  private osGroups: OsFileGroup[] = [];
  /** Observaciones por OS (se conservan al reconstruir los grupos). */
  private osObservaciones = new Map<string, string>();

  /** Agrupación jerárquica estable: File → Fecha de Pago → Moneda → Código de Reserva. */
  get fileOsGroups(): OsFileGroup[] {
    return this.osGroups;
  }

  /** Reconstruye la agrupación de OS a partir del estado actual de itinerarios/servicios. */
  private buildOsGroups(): OsFileGroup[] {
    const fileMap = new Map<
      string,
      Map<string, Map<string, Map<string, OsCodigoReservaGroup>>>
    >();

    for (const it of this.itineraryStates) {
      for (const svc of it.serviceStates) {
        const units = this.paymentUnits(svc, it);
        for (const unit of units) {
          const fecha = unit.paymentDate;
          const fechaKey = `${fecha.getTime()}`;
          const codigo = unit.codigoReserva?.trim() || '';

          if (!fileMap.has(it.fileNumber)) {
            fileMap.set(it.fileNumber, new Map());
          }
          const fechaMap = fileMap.get(it.fileNumber)!;
          if (!fechaMap.has(fechaKey)) {
            fechaMap.set(fechaKey, new Map());
          }
          const monedaMap = fechaMap.get(fechaKey)!;
          if (!monedaMap.has(unit.moneda)) {
            monedaMap.set(unit.moneda, new Map());
          }
          const codigoMap = monedaMap.get(unit.moneda)!;
          if (!codigoMap.has(codigo)) {
            codigoMap.set(codigo, {
              fileNumber: it.fileNumber,
              moneda: unit.moneda,
              codigoReserva: codigo,
              fechaPago: fecha,
              services: [],
              units: [],
              total: 0,
              observaciones:
                this.osObservaciones.get(
                  this.osKey(it.fileNumber, fecha, unit.moneda, codigo)
                ) ?? ''
            });
          }
          const group = codigoMap.get(codigo)!;
          if (!group.services.some((s) => s.serviceId === svc.serviceId)) {
            group.services.push(svc);
          }
          group.units.push(unit);
          group.total += unit.total;
        }
      }
    }

    const result: OsFileGroup[] = [];
    const files = Array.from(fileMap.keys()).sort();
    let osSeq = 0;
    for (const fileNumber of files) {
      const fechaMap = fileMap.get(fileNumber)!;
      const fechaKeys = Array.from(fechaMap.keys()).sort(
        (a, b) => Number(a) - Number(b)
      );
      const fechaGrupos: OsFechaPagoGroup[] = fechaKeys.map((fechaKey) => {
        const monedaMap = fechaMap.get(fechaKey)!;
        const monedaKeys = Array.from(monedaMap.keys()).sort();
        const monedaGrupos: OsMonedaGroup[] = monedaKeys.map((moneda) => {
          const codigoMap = monedaMap.get(moneda)!;
          const codigoKeys = Array.from(codigoMap.keys()).sort();
          const codigoGrupos = codigoKeys.map(
            (codigo) => codigoMap.get(codigo)!
          );
          codigoGrupos.forEach((group) => {
            group.seq = ++osSeq;
            const pay = this.isOsMode
              ? this.previewGroupCommission(group)
              : this.previewCommission(group.total);
            group.commissionPercent = pay?.percent ?? null;
            group.commissionAmount = pay?.amount ?? null;
            group.payableTotal = pay?.total ?? null;
            group.commissionLabel = pay?.label ?? null;
          });
          return { moneda: moneda as MassOsCurrency, codigoGrupos };
        });
        return {
          fechaPago: monedaGrupos[0]?.codigoGrupos[0]?.fechaPago ?? new Date(),
          monedaGrupos
        };
      });
      result.push({ fileNumber, fechaGrupos });
    }
    this.osGroups = result;
    return result;
  }

  /** Devuelve las unidades de pago de un servicio: 1 por ticket (TICKET) o 1 por servicio. */
  private paymentUnits(
    svc: MassOsServicePaymentState,
    it: MassOsItineraryPaymentState
  ): OsUnitState[] {
    if (svc.isTicket && svc.tickets?.length) {
      // Cada ticket se agrupa por SUS propios valores (fecha, moneda, código),
      // el mismo criterio que los servicios normales.
      return svc.tickets.map((t) => ({
        kind: 'ticket' as const,
        serviceId: svc.serviceId,
        ticketId: t.ticketId,
        moneda: t.moneda,
        total: t.total,
        nombreServicio: t.nombreServicio,
        nombre: svc.nombre,
        serviceDay: t.serviceDay,
        fecha: t.fecha,
        paymentDate: this.ticketFinalDate(t, it),
        codigoReserva: t.codigoReserva
      }));
    }
    return [
      {
        kind: 'service' as const,
        serviceId: svc.serviceId,
        moneda: svc.moneda,
        total: svc.costo,
        nombreServicio: svc.nombreServicio,
        nombre: svc.nombre,
        serviceDay: svc.serviceDay,
        fecha: svc.fecha,
        paymentDate: this.getFinalDate(svc, it),
        codigoReserva: svc.codigoReserva
      }
    ];
  }

  private osKey(
    fileNumber: string,
    fechaPago: Date,
    moneda: MassOsCurrency,
    codigoReserva: string
  ): string {
    return `${fileNumber}|${fechaPago.getTime()}|${moneda}|${codigoReserva}`;
  }

  get totalOs(): number {
    return this.fileOsGroups.reduce(
      (sum, file) =>
        sum +
        file.fechaGrupos.reduce(
          (s, f) =>
            s +
            f.monedaGrupos.reduce(
              (m, moneda) => m + moneda.codigoGrupos.length,
              0
            ),
          0
        ),
      0
    );
  }

  get currencyTotals(): Array<{ moneda: MassOsCurrency; total: number }> {
    const totals = new Map<MassOsCurrency, number>();
    for (const it of this.itineraryStates) {
      for (const svc of it.serviceStates) {
        totals.set(svc.moneda, (totals.get(svc.moneda) ?? 0) + svc.costo);
      }
    }
    return Array.from(totals.entries()).map(([moneda, total]) => ({
      moneda,
      total
    }));
  }

  commissionEnabled = false;
  asOfficial = false;
  freezeDeadlineAt = '';
  freezeDeadlineTouched = false;
  confirmingOrderId: string | null = null;

  get freezeDeadlineError(): string | null {
    if (!this.isOsMode || this.asOfficial) {
      return null;
    }
    return this.freezeDeadlineAt
      ? null
      : 'Elige una fecha: el plazo es obligatorio.';
  }

  private blockIfFreezeDeadlineMissing(): boolean {
    if (!this.freezeDeadlineError) {
      return false;
    }
    this.freezeDeadlineTouched = true;
    this.activeStep = 2;
    return true;
  }

  /** Plazo para confirmar el lote como Date para el matDatepicker (mismo UX que Fecha de Pago). */
  get freezeDeadlineDate(): Date | null {
    if (!this.freezeDeadlineAt) {
      return null;
    }
    const parsed = new Date(`${this.freezeDeadlineAt}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  applyFreezeDeadline(value: Date | null): void {
    if (!value) {
      // Obligatorio: el plazo no se puede dejar sin fecha.
      return;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    this.freezeDeadlineAt = `${year}-${month}-${day}`;
  }
  commissionType: 'PERCENTAGE' | 'FIXED' = 'PERCENTAGE';
  commissionValue: number | null = null;
  commissionValueTouched = false;

  get isCommissionStep(): boolean {
    return this.activeStep === (this.isOsMode ? 2 : 1);
  }

  get showCommissionError(): boolean {
    return this.commissionValueTouched && Boolean(this.commissionError);
  }

  get commissionError(): string | null {
    if (!this.commissionEnabled) {
      return null;
    }
    const value = Number(this.commissionValue);
    if (!Number.isFinite(value) || value <= 0) {
      return 'Ingresa un valor mayor a 0.';
    }
    if (this.commissionType === 'PERCENTAGE' && value > 100) {
      return 'El porcentaje no puede ser mayor a 100.';
    }
    return null;
  }

  markCommissionTouched(): void {
    this.commissionValueTouched = true;
  }

  setCommissionEnabled(enabled: boolean): void {
    this.commissionEnabled = enabled;
    if (!enabled) {
      this.commissionValueTouched = false;
      return;
    }
    if (
      this.isOsMode &&
      this.commissionType === 'PERCENTAGE' &&
      this.commissionValue == null
    ) {
      this.commissionValue = 2.91;
    }
  }

  setCommissionType(type: 'PERCENTAGE' | 'FIXED'): void {
    this.commissionType = type;
    if (!this.isOsMode) {
      return;
    }
    if (type === 'FIXED' && this.isDefaultPercent(this.commissionValue)) {
      this.commissionValue = null;
      return;
    }
    if (
      type === 'PERCENTAGE' &&
      this.commissionEnabled &&
      this.commissionValue == null
    ) {
      this.commissionValue = 2.91;
    }
  }

  private isDefaultPercent(value: number | null): boolean {
    return value != null && Math.round(Number(value) * 100) === 291;
  }

  onCommissionValueInput(raw: string): void {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      this.commissionValue = null;
      return;
    }
    const value = Number(trimmed);
    this.commissionValue = Number.isFinite(value) ? value : null;
  }

  /** Suma la comisión de cada fila. El porcentaje no se aplica al total del grupo. */
  previewGroupCommission(
    group: OsCodigoReservaGroup
  ): { label: string; percent: number | null; amount: number; total: number } | null {
    if (!this.commissionEnabled || this.commissionError) {
      return null;
    }
    let amount = 0;
    let base = 0;
    for (const unit of group.units) {
      const row = this.previewCommission(unit.total);
      if (!row) {
        return null;
      }
      amount += row.amount;
      base += unit.total;
    }
    const roundedAmount = this.roundMoney(amount);
    const roundedBase = this.roundMoney(base);
    const sample = this.previewCommission(roundedBase > 0 ? roundedBase : 0);
    return {
      label:
        this.commissionType === 'PERCENTAGE' && sample
          ? sample.label
          : 'Comisión',
      percent: this.commissionType === 'PERCENTAGE' ? this.commissionValue : null,
      amount: roundedAmount,
      total: this.roundMoney(roundedBase + roundedAmount)
    };
  }

  /**
   * Comisión de una fila. Apagada o inválida → null.
   */
  previewCommission(
    base: number
  ): { label: string; percent: number | null; amount: number; total: number } | null {
    if (!this.commissionEnabled || this.commissionError) {
      return null;
    }
    const value = this.roundMoney(Number(this.commissionValue));
    const amount =
      this.commissionType === 'PERCENTAGE'
        ? this.roundMoney((base * value) / 100)
        : value;
    const percent =
      this.commissionType === 'PERCENTAGE'
        ? value
        : base > 0
          ? this.roundMoney((amount / base) * 100)
          : null;
    const percentText =
      percent == null
        ? null
        : Number.isInteger(percent)
          ? String(percent)
          : String(percent);
    return {
      label: percentText == null ? 'Comisión' : `Comisión (${percentText}%)`,
      percent,
      amount,
      total: this.roundMoney(base + amount)
    };
  }

  get payableCurrencyTotals(): Array<{ moneda: MassOsCurrency; total: number }> {
    if (this.isOsMode && this.osGroups.length > 0) {
      const totals = new Map<MassOsCurrency, number>();
      for (const file of this.osGroups) {
        for (const fecha of file.fechaGrupos) {
          for (const moneda of fecha.monedaGrupos) {
            for (const group of moneda.codigoGrupos) {
              const amount = group.payableTotal ?? group.total;
              totals.set(
                group.moneda,
                (totals.get(group.moneda) ?? 0) + amount
              );
            }
          }
        }
      }
      return Array.from(totals.entries()).map(([moneda, total]) => ({
        moneda,
        total
      }));
    }
    return this.currencyTotals.map((row) => ({
      moneda: row.moneda,
      total: this.previewCommission(row.total)?.total ?? row.total
    }));
  }

  private roundMoney(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private buildCommissionPayload():
    | { type: 'PERCENTAGE' | 'FIXED'; value: number }
    | undefined {
    if (!this.commissionEnabled || this.commissionError) {
      return undefined;
    }
    return {
      type: this.commissionType,
      value: Number(this.commissionValue)
    };
  }

  private blockIfCommissionInvalid(): boolean {
    if (!this.commissionEnabled || !this.commissionError) {
      return false;
    }
    this.commissionValueTouched = true;
    this.activeStep = this.isOsMode ? 2 : 1;
    return true;
  }

  nextStep(): void {
    if (this.isCommissionStep && this.commissionError) {
      this.commissionValueTouched = true;
      return;
    }
    if (this.activeStep < this.steps.length - 1) {
      this.activeStep++;
      if (this.activeStep === this.steps.length - 1) {
        this.buildOsGroups();
      }
    }
  }

  prevStep(): void {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  }

  /* ─── Fecha de pago (Paso 1) ─── */

  applyMassDate(itineraryId: string, date: Date | null): void {
    const state = this.itineraryStates.find(
      (s) => s.itineraryId === itineraryId
    );
    if (!state) return;

    state.fechaMasiva = date;
    for (const svc of state.serviceStates) {
      if (svc.fechaManual === null) {
        svc.status = date !== null ? 'cambio-masivo' : 'automatico';
      }
      for (const ticket of svc.tickets ?? []) {
        if (ticket.fechaManual === null) {
          ticket.status = date !== null ? 'cambio-masivo' : 'automatico';
        }
      }
    }
  }

  updateServiceDate(
    itineraryId: string,
    serviceId: string,
    date: Date | null,
    ticketId?: string | null
  ): void {
    const state = this.itineraryStates.find(
      (s) => s.itineraryId === itineraryId
    );
    if (!state) return;

    const svc = state.serviceStates.find((s) => s.serviceId === serviceId);
    if (!svc) return;

    const ticket = ticketId
      ? (svc.tickets ?? []).find((t) => t.ticketId === ticketId)
      : null;
    if (ticket) {
      ticket.fechaManual = date;
      ticket.status = date !== null ? 'modificado' : this.resolveTicketStatus(state, ticket);
      return;
    }

    svc.fechaManual = date;
    svc.status = date !== null ? 'modificado' : this.resolveStatus(state, svc);
  }

  /* ─── Código de reserva (Paso 2) ─── */

  applyMassCode(itineraryId: string, code: string): void {
    const state = this.itineraryStates.find(
      (s) => s.itineraryId === itineraryId
    );
    if (!state) return;

    state.codigoReservaMasivo = code || null;
    let skipped = 0;
    for (const svc of state.serviceStates) {
      if (svc.codigoManual) {
        skipped++;
      } else {
        svc.codigoReserva = code || null;
      }
      for (const ticket of svc.tickets ?? []) {
        if (ticket.codigoManual) {
          skipped++;
        } else {
          ticket.codigoReserva = code || null;
        }
      }
    }
    this.massCodeFedBack = skipped > 0;
  }

  /** true si la última aplicación de código masivo omitió códigos manuales. */
  massCodeFedBack = false;

  /* ─── Feedback de masivo (cuántos servicios se ven afectados) ─── */

  private unitCount(svc: MassOsServicePaymentState): number {
    if (svc.isTicket && svc.tickets?.length) {
      return svc.tickets.length;
    }
    return 1;
  }

  /** Nº de unidades (servicios/tickets) ajustadas por fecha masiva (las manuales se conservan). */
  massDateAffected(state: MassOsItineraryPaymentState): number {
    return state.serviceStates.reduce((acc, svc) => {
      if (svc.fechaManual !== null) {
        return acc;
      }
      return acc + this.unitCount(svc);
    }, 0);
  }

  /** Nº de unidades que conservan fecha manual tras aplicar fecha masiva. */
  massDateSkip(state: MassOsItineraryPaymentState): number {
    return state.serviceStates.reduce((acc, svc) => {
      if (svc.fechaManual === null) {
        return acc;
      }
      return acc + this.unitCount(svc);
    }, 0);
  }

  /** Nº de unidades (servicios/tickets) a las que se aplicó el código masivo. */
  massCodeAffected(state: MassOsItineraryPaymentState): number {
    return state.serviceStates.reduce((acc, svc) => {
      if (svc.codigoManual) {
        return acc;
      }
      return acc + this.unitCount(svc);
    }, 0);
  }

  /** Nº de unidades que conservan código manual tras aplicar código masivo. */
  massCodeSkip(state: MassOsItineraryPaymentState): number {
    return state.serviceStates.reduce((acc, svc) => {
      if (!svc.codigoManual) {
        return acc;
      }
      return acc + this.unitCount(svc);
    }, 0);
  }

  updateServiceCode(
    itineraryId: string,
    serviceId: string,
    code: string,
    ticketId?: string | null
  ): void {
    const state = this.itineraryStates.find(
      (s) => s.itineraryId === itineraryId
    );
    if (!state) return;

    const svc = state.serviceStates.find((s) => s.serviceId === serviceId);
    if (!svc) return;

    if (ticketId) {
      const ticket = (svc.tickets ?? []).find((t) => t.ticketId === ticketId);
      if (ticket) {
        ticket.codigoReserva = code || null;
        ticket.codigoManual = code.trim() !== '';
      }
      return;
    }

    svc.codigoReserva = code || null;
    svc.codigoManual = code.trim() !== '';
  }

  /* ─── Acciones del footer ─── */

  cancel(): void {
    if (this.viewState === 'generating') {
      return;
    }
    if (this.hasUnsavedChanges) {
      const ok = window.confirm(
        'Tienes cambios sin guardar (fechas, códigos de reserva u observaciones).\n\n¿Deseas salir sin guardar?'
      );
      if (!ok) {
        return;
      }
    }
    this.dialogRef.close({ confirmed: false, itineraryStates: [] });
  }

  /** Indica si el usuario ha editado fechas, códigos u observaciones sin generar. */
  get hasUnsavedChanges(): boolean {
    for (const it of this.itineraryStates) {
      if (it.fechaMasiva || it.codigoReservaMasivo) {
        return true;
      }
      for (const svc of it.serviceStates) {
        if (svc.fechaManual || svc.codigoReserva || svc.observaciones || svc.codigoManual) {
          return true;
        }
        for (const ticket of svc.tickets ?? []) {
          if (ticket.fechaManual || ticket.codigoReserva || ticket.codigoManual || ticket.observaciones) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /** Muestra la vista de doble confirmación antes de generar las OS. */
  confirm(): void {
    if (this.viewState === 'generating' || this.isLastStep === false) {
      return;
    }
    if (this.blockIfCommissionInvalid()) {
      return;
    }
    if (this.blockIfFreezeDeadlineMissing()) {
      return;
    }
    if (this.totalOs === 0 || this.totalServices === 0) {
      return;
    }
    this.confirming = true;
  }

  /** Regresa de la vista de confirmación al resumen. */
  backFromConfirm(): void {
    if (this.viewState === 'generating') {
      return;
    }
    this.confirming = false;
  }

  /** Inicia la generación real de OS/NP por cada grupo del resumen. */
  doGenerate(): void {
    if (this.blockIfCommissionInvalid()) {
      this.confirming = false;
      return;
    }
    if (this.blockIfFreezeDeadlineMissing()) {
      this.confirming = false;
      return;
    }
    this.confirming = false;

    this.propagateObservacionesToServices();

    const usuario =
      `${localStorage.getItem('nombre') ?? ''} ${localStorage.getItem('apellido') ?? ''}`.trim();
    const email = localStorage.getItem('email') ?? '';

    if (this.isNpMode) {
      const inputs = this.buildCreatePurchaseNoteInputs(usuario, email);
      if (!inputs.length) {
        this.dialogRef.close({
          confirmed: true,
          itineraryStates: this.itineraryStates
        });
        return;
      }
      this.lastPurchaseNoteInputs = inputs;
      this.runCreatePurchaseNotes(inputs);
      return;
    }

    const inputs = this.buildCreateOrderInputs(usuario, email);
    if (!inputs.length) {
      this.dialogRef.close({
        confirmed: true,
        itineraryStates: this.itineraryStates
      });
      return;
    }

    this.lastInputs = inputs;
    this.runCreateOrders(inputs);
  }

  /** Propaga las observaciones de cada OS/NP a sus servicios (para el resumen del componente). */
  private propagateObservacionesToServices(): void {
    for (const fileGroup of this.fileOsGroups) {
      for (const fechaGroup of fileGroup.fechaGrupos) {
        for (const monedaGroup of fechaGroup.monedaGrupos) {
          for (const group of monedaGroup.codigoGrupos) {
            for (const svc of group.services) {
              svc.observaciones = group.observaciones;
            }
          }
        }
      }
    }
  }

  /** Reintenta la creación tras un error de job. */
  retry(): void {
    if (this.viewState === 'generating') {
      return;
    }
    if (this.isNpMode) {
      if (!this.lastPurchaseNoteInputs.length) {
        return;
      }
      this.runCreatePurchaseNotes(this.lastPurchaseNoteInputs);
      return;
    }
    if (!this.lastInputs.length) {
      return;
    }
    this.runCreateOrders(this.lastInputs);
  }

  /** Cierra el dialog devolviendo el resultado de la creación. */
  cerrar(): void {
    const result =
      this.results?.kind === 'np'
        ? {
            createdPurchaseNotes: this.results.value.createdPurchaseNotes ?? [],
            rejectedServices: this.results.value.rejectedServices ?? [],
            warnings: this.results.value.warnings ?? []
          }
        : {
            createdOrders: this.result?.createdOrders ?? [],
            rejectedServices: this.result?.rejectedServices ?? [],
            warnings: this.result?.warnings ?? []
          };

    this.dialogRef.close({
      confirmed: true,
      itineraryStates: this.itineraryStates,
      ...result,
      createdCount: this.createdCountInternal
    });
  }

  /* ─── Resultado (vista 'result' / 'error') ─── */

  get isGenerating(): boolean {
    return this.viewState === 'generating';
  }

  get creationResult(): CreateOrderResult | null {
    return this.result;
  }

  confirmarOs(orderId: string): void {
    if (!orderId || this.confirmingOrderId || !this.result) {
      return;
    }
    this.confirmingOrderId = orderId;
    const sub = this.orderService.confirmPreliminaryOrder(orderId).subscribe({
      next: (confirmed) => {
        this.confirmingOrderId = null;
        if (!this.result || !confirmed) {
          return;
        }
        this.result = {
          ...this.result,
          createdOrders: this.result.createdOrders.map((item) =>
            item.orderId === orderId
              ? {
                  ...item,
                  orderStatus: confirmed.orderStatus,
                  displayCode: confirmed.displayCode || item.displayCode,
                  pdfStatus: confirmed.pdfStatus
                }
              : item
          )
        };
      },
      error: () => {
        this.confirmingOrderId = null;
      }
    });
    this.subscriptions.push(sub);
  }

  /** Resultado NP (para la lista de NP creadas). */
  get creationPurchaseNoteResult(): CreatePurchaseNoteResult | null {
    return this.results?.kind === 'np' ? this.results.value : null;
  }

  get npPayRows(): Array<{
    number: string;
    label: string;
    base: number;
    amount: number | null;
    total: number;
  }> {
    const notes = this.creationPurchaseNoteResult?.createdPurchaseNotes ?? [];
    return notes.flatMap((note) => {
      const row = this.payRowForNote(note);
      return row ? [row] : [];
    });
  }

  private payRowForNote(note: CreatedPurchaseNote): {
    number: string;
    label: string;
    base: number;
    amount: number | null;
    total: number;
  } | null {
    const submitted = this.buildCommissionPayload();
    let base =
      note.baseAmount != null && Number.isFinite(Number(note.baseAmount))
        ? Number(note.baseAmount)
        : null;
    let amount =
      note.commissionAmount != null && Number.isFinite(Number(note.commissionAmount))
        ? Number(note.commissionAmount)
        : null;
    let total =
      note.total != null && Number.isFinite(Number(note.total))
        ? Number(note.total)
        : null;
    let percent =
      note.commissionPercent != null && Number.isFinite(Number(note.commissionPercent))
        ? Number(note.commissionPercent)
        : null;

    if (amount == null && submitted) {
      if (base == null && total != null) {
        base = total;
      }
      if (base == null) {
        return null;
      }
      const value = Number(submitted.value);
      amount =
        submitted.type === 'PERCENTAGE'
          ? this.roundMoney((base * value) / 100)
          : this.roundMoney(value);
      percent = submitted.type === 'PERCENTAGE' ? value : percent;
      total = this.roundMoney(base + amount);
    }

    if (amount == null || base == null) {
      const plain = base ?? total;
      if (plain == null) {
        return null;
      }
      return {
        number: note.purchaseNoteNumber,
        label: '',
        base: plain,
        amount: null,
        total: total ?? plain
      };
    }
    if (total == null) {
      total = this.roundMoney(base + amount);
    }
    if (percent == null && base > 0) {
      percent = this.roundMoney((amount / base) * 100);
    }
    const percentText =
      percent == null
        ? null
        : Number.isInteger(percent)
          ? String(percent)
          : String(percent);

    return {
      number: note.purchaseNoteNumber,
      label: percentText == null ? 'Comisión' : `Comisión (${percentText}%)`,
      base,
      amount,
      total
    };
  }

  get creationErrorMessage(): string | null {
    return this.errorMessage;
  }

  get createdCount(): number {
    return this.createdCountInternal;
  }

  get creationErrorTitle(): string | null {
    return this.errorTitle;
  }

  get hasCreated(): boolean {
    if (this.results?.kind === 'np') {
      return (this.results.value.createdPurchaseNotes?.length ?? 0) > 0;
    }
    return (this.result?.createdOrders?.length ?? 0) > 0;
  }

  get hasWarnings(): boolean {
    if (this.results?.kind === 'np') {
      return (this.results.value.warnings?.length ?? 0) > 0;
    }
    return (this.result?.warnings?.length ?? 0) > 0;
  }

  get hasRejections(): boolean {
    if (this.results?.kind === 'np') {
      return (this.results.value.rejectedServices?.length ?? 0) > 0;
    }
    return (this.result?.rejectedServices?.length ?? 0) > 0;
  }

  get rejectionCards(): Array<{
    title: string;
    body: string;
    serviceId: string | null;
  }> {
    if (this.results?.kind === 'np') {
      return (this.results.value.rejectedServices ?? []).map((rejected) => {
        const msg = resolveCreatePurchaseNoteRejectionMessage(rejected);
        return {
          title: msg.title,
          body: formatUserMessageText(msg),
          serviceId: rejected.serviceId?.trim() || null
        };
      });
    }

    return (this.result?.rejectedServices ?? []).map((rejected) => {
      const msg = resolveCreateOrderRejectionMessage(rejected);
      return {
        title: msg.title,
        body: formatUserMessageText(msg),
        serviceId: rejected.serviceId?.trim() || null
      };
    });
  }

  get warningCards(): Array<{ title: string; body: string }> {
    const seen = new Set<string>();

    if (this.results?.kind === 'np') {
      return (this.results.value.warnings ?? [])
        .filter((warning) => {
          const key = warning.reason || 'UNKNOWN';
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        })
        .map((warning) => {
          const msg = resolveCreatePurchaseNoteWarningMessage(warning);
          return { title: msg.title, body: formatUserMessageText(msg) };
        });
    }

    return (this.result?.warnings ?? [])
      .filter((warning) => {
        const key = warning.reason || 'UNKNOWN';
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map((warning) => {
        const msg = resolveCreateOrderWarningMessage(warning);
        return { title: msg.title, body: formatUserMessageText(msg) };
      });
  }

  /* ─── Helpers ─── */

  formatShortDate(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  /**
   * Construye un CreateOrderInput por cada segmento de OS del resumen
   * (File → Fecha de Pago → Moneda → Código de Reserva).
   */
  private buildCreateOrderInputs(
    usuario: string,
    email: string
  ): CreateOrderInput[] {
    const results: CreateOrderInput[] = [];

    for (const fileGroup of this.fileOsGroups) {
      for (const fechaGroup of fileGroup.fechaGrupos) {
        for (const monedaGroup of fechaGroup.monedaGrupos) {
          for (const group of monedaGroup.codigoGrupos) {
            const items = this.buildOrderItems(group);
            if (!items.length) {
              continue;
            }

            const reservationNumber = this.resolveGroupReservationNumber(group);

            const commission = this.buildCommissionPayload();
            results.push({
              items,
              createdBy: { name: usuario, email },
              scheduledPaymentDate: toScheduledPaymentDateIso(group.fechaPago),
              asOfficial: this.asOfficial,
              freezeDeadlineAt:
                !this.asOfficial && this.freezeDeadlineAt
                  ? `${this.freezeDeadlineAt}T00:00:00.000Z`
                  : null,
              reservationNumber: reservationNumber?.trim() || null,
              orderObservation: group.observaciones?.trim() || null,
              ...(commission ? { commission } : {})
            });
          }
        }
      }
    }

    return results;
  }

  /**
   * Construye los ítems de un OS. Servicio normal → `{ serviceId }`. Servicio
   * TICKET con tickets desglosados → `{ serviceId, ticketIds }` con los ids
   * explícitos (contrato Ticketería, no fan-out). Si el TICKET se eligió sin
   * tickets (solo el servicio) se envía `{ serviceId }` (fan-out de los libres).
   */
  private buildOrderItems(group: OsCodigoReservaGroup): CreateOrderItemInput[] {
    const ticketIdsByService = new Map<string, string[]>();
    const serviceOrder: string[] = [];
    for (const unit of group.units) {
      if (!ticketIdsByService.has(unit.serviceId)) {
        ticketIdsByService.set(unit.serviceId, []);
        serviceOrder.push(unit.serviceId);
      }
      if (unit.kind === 'ticket' && unit.ticketId) {
        const ids = ticketIdsByService.get(unit.serviceId)!;
        if (!ids.includes(unit.ticketId)) {
          ids.push(unit.ticketId);
        }
      }
    }
    return serviceOrder.map((serviceId) => {
      const ticketIds = ticketIdsByService.get(serviceId)!;
      return ticketIds.length > 0
        ? { serviceId, ticketIds }
        : { serviceId };
    });
  }

  /** Asigna las observaciones ingresadas en el resumen a una OS concreta. */
  updateOsObservaciones(group: OsCodigoReservaGroup, value: string): void {
    group.observaciones = value ?? '';
    this.osObservaciones.set(
      this.osKey(
        group.fileNumber,
        group.fechaPago,
        group.moneda,
        group.codigoReserva
      ),
      (value ?? '').trim()
    );
  }

  /** Código de reserva del grupo: el del segmento > masivo del itinerario > dato del primer servicio. */
  private resolveGroupReservationNumber(
    group: OsCodigoReservaGroup
  ): string | null {
    if (group.codigoReserva?.trim()) {
      return group.codigoReserva;
    }
    for (const svc of group.services) {
      if (svc.codigoReserva?.trim()) {
        return svc.codigoReserva;
      }
    }
    return null;
  }

  /** Ejecuta createOrderWithPolling por cada input y acumula el resultado. */
  private runCreateOrders(inputs: CreateOrderInput[]): void {
    const createdOrders: CreatedOrder[] = [];
    const rejectedServices: RejectedService[] = [];
    const warnings: CreateOrderResult['warnings'] = [];

    this.viewState = 'generating';
    this.errorTitle = null;
    this.errorMessage = null;
    this.dialogRef.disableClose = true;

    let index = 0;
    const next = (): void => {
      if (index >= inputs.length) {
        this.dialogRef.disableClose = false;
        this.createdCountInternal = createdOrders.length;
        this.result = { createdOrders, rejectedServices, warnings };
        this.viewState =
          createdOrders.length > 0 || rejectedServices.length === 0
            ? 'result'
            : 'error';
        return;
      }

      const input = inputs[index];
      index += 1;

      const createdSub = this.orderService
        .createOrderWithPolling(input)
        .subscribe({
          next: (job) => {
            const part = toCreateOrderResult(job);
            createdOrders.push(...(part.createdOrders ?? []));
            rejectedServices.push(...(part.rejectedServices ?? []));
            warnings.push(...(part.warnings ?? []));
            next();
          },
          error: (error) => {
            logOrderDevDetail('mass-os.createOrder.error', error);
            const msg = resolveOrderGraphqlUserMessage(
              error,
              'No se pudo crear las órdenes. Reintenta o contacta a Soporte TI.'
            );
            this.errorTitle = msg.title;
            this.errorMessage = formatUserMessageText(msg);
            this.dialogRef.disableClose = false;
            this.viewState = 'error';
          }
        });

      this.subscriptions.push(createdSub);
    };

    next();
  }

  /** Ejecuta createPurchaseNoteWithPolling por cada input y acumula el resultado. */
  private runCreatePurchaseNotes(inputs: CreatePurchaseNoteInput[]): void {
    const createdPurchaseNotes: CreatedPurchaseNote[] = [];
    const rejectedServices: CreatePurchaseNoteResult['rejectedServices'] = [];
    const warnings: CreatePurchaseNoteResult['warnings'] = [];

    this.viewState = 'generating';
    this.errorTitle = null;
    this.errorMessage = null;
    this.dialogRef.disableClose = true;

    let index = 0;
    const next = (): void => {
      if (index >= inputs.length) {
        this.dialogRef.disableClose = false;
        this.createdCountInternal = createdPurchaseNotes.length;
        this.results = {
          kind: 'np',
          value: { createdPurchaseNotes, rejectedServices, warnings }
        };
        this.viewState =
          createdPurchaseNotes.length > 0 || rejectedServices.length === 0
            ? 'result'
            : 'error';
        return;
      }

      const input = inputs[index];
      index += 1;

      const createdSub = this.purchaseNoteService
        .createPurchaseNoteWithPolling(input)
        .subscribe({
          next: (job) => {
            const part = toCreatePurchaseNoteResult(job);
            createdPurchaseNotes.push(...(part.createdPurchaseNotes ?? []));
            rejectedServices.push(...(part.rejectedServices ?? []));
            warnings.push(...(part.warnings ?? []));
            next();
          },
          error: (error) => {
            logOrderDevDetail('mass-os.createPurchaseNote.error', error);
            const msg = resolvePurchaseNoteGraphqlUserMessage(error);
            this.errorTitle = msg.title;
            this.errorMessage = formatUserMessageText(msg);
            this.dialogRef.disableClose = false;
            this.viewState = 'error';
          }
        });

      this.subscriptions.push(createdSub);
    };

    next();
  }

  /**
   * Construye un CreatePurchaseNoteInput por cada segmento del resumen
   * (File → Fecha de Revisión → Moneda → Código de Reserva). Fecha de revisión
   * = fecha del grupo (misma semántica de agrupación que OS, pero etiquetada
   * como revisión).
   */
  private buildCreatePurchaseNoteInputs(
    usuario: string,
    email: string
  ): CreatePurchaseNoteInput[] {
    const results: CreatePurchaseNoteInput[] = [];

    for (const fileGroup of this.fileOsGroups) {
      for (const fechaGroup of fileGroup.fechaGrupos) {
        for (const monedaGroup of fechaGroup.monedaGrupos) {
          for (const group of monedaGroup.codigoGrupos) {
            const items = this.buildPurchaseNoteItems(group);
            if (!items.length) {
              continue;
            }

            const commission = this.buildCommissionPayload();
            results.push({
              items,
              createdBy: { name: usuario, email },
              reviewDate: this.toDateOnlyInput(group.fechaPago),
              purchaseNoteObservation: group.observaciones?.trim() || null,
              ...(commission ? { commission } : {})
            });
          }
        }
      }
    }

    return results;
  }

  /** Ítems de una NP a partir de las unidades del grupo (mismo criterio que OS). */
  private buildPurchaseNoteItems(
    group: OsCodigoReservaGroup
  ): Array<{ serviceId: string; ticketIds?: string[] | null }> {
    const ticketIdsByService = new Map<string, string[]>();
    const serviceOrder: string[] = [];
    for (const unit of group.units) {
      if (!ticketIdsByService.has(unit.serviceId)) {
        ticketIdsByService.set(unit.serviceId, []);
        serviceOrder.push(unit.serviceId);
      }
      if (unit.kind === 'ticket' && unit.ticketId) {
        const ids = ticketIdsByService.get(unit.serviceId)!;
        if (!ids.includes(unit.ticketId)) {
          ids.push(unit.ticketId);
        }
      }
    }
    return serviceOrder.map((serviceId) => {
      const ticketIds = ticketIdsByService.get(serviceId)!;
      return ticketIds.length > 0
        ? { serviceId, ticketIds }
        : { serviceId };
    });
  }

  /** Fecha calendario YYYY-MM-DD (para reviewDate de NP). */
  private toDateOnlyInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getFinalDate(
    svc: MassOsServicePaymentState,
    it?: MassOsItineraryPaymentState
  ): Date {
    if (svc.fechaManual) return svc.fechaManual;
    if (it?.fechaMasiva) return it.fechaMasiva;
    return svc.fechaAutomatica;
  }

  /** Fecha final de pago de un ticket (manual → masiva → automática). */
  private ticketFinalDate(
    ticket: MassOsTicketPaymentState,
    it?: MassOsItineraryPaymentState
  ): Date {
    if (ticket.fechaManual) return ticket.fechaManual;
    if (it?.fechaMasiva) return it.fechaMasiva;
    return ticket.fechaAutomatica;
  }

  /* ─── Privados ─── */

  private buildInitialState(): MassOsItineraryPaymentState[] {
    const selectedSet = new Set(this.data.selectedServiceIds);
    const selectedTicketMap = this.data.selectedTickets ?? {};
    const advanceDays = this.condicionPagoDias;

    return this.data.itineraries
      .map((itinerary) => {
        const serviceStates: MassOsServicePaymentState[] = itinerary.servicios
          .filter((s) => selectedSet.has(s.id))
          .map((s) => {
            const fechaAutomatica = this.calcAutoDate(advanceDays);

            // Servicios TICKET con tickets seleccionados → desglose por ticket.
            const isTicketService =
              String(s.tipoServicio ?? '').toUpperCase() === 'TICKET';
            const ticketIds = (selectedTicketMap[s.id] ?? []) as string[];
            if (isTicketService && ticketIds.length > 0) {
              const tickets = this.buildTicketStates(s, ticketIds, advanceDays);
              return {
                serviceId: s.id,
                serviceDay: s.serviceDay,
                fecha: s.fecha,
                tipoServicio: s.tipoServicio,
                nombreServicio: s.nombreServicio,
                nombre: s.nombre,
                moneda: s.moneda,
                pax: s.pax,
                costo: s.costo,
                fechaAutomatica,
                fechaManual: null,
                status: 'automatico' as const,
                codigoReserva: null,
                codigoManual: false,
                isTicket: true,
                tickets
              };
            }
            return {
              serviceId: s.id,
              serviceDay: s.serviceDay,
              fecha: s.fecha,
              tipoServicio: s.tipoServicio,
              nombreServicio: s.nombreServicio,
              nombre: s.nombre,
              moneda: s.moneda,
              pax: s.pax,
              costo: s.costo,
              fechaAutomatica,
              fechaManual: null,
              status: 'automatico' as const,
              codigoReserva: null,
              codigoManual: false
            };
          });

        return {
          itineraryId: itinerary.id,
          fileNumber: itinerary.fileNumber,
          agencia: itinerary.agencia,
          usuario: itinerary.usuario,
          fechaMasiva: null,
          codigoReservaMasivo: null,
          serviceStates
        };
      })
      .filter((s) => s.serviceStates.length > 0);
  }

  /** Construye el estado de pago por ticket para un servicio TICKET. */
  private buildTicketStates(
    service: {
      serviceDay: number;
      fecha: Date;
      nombreServicio: string;
      tickets?: Array<{ id?: string; total?: number | null; moneda?: string | null }> | null;
    },
    ticketIds: string[],
    advanceDays: number
  ): MassOsTicketPaymentState[] {
    const ticketsBy = new Map<string, { total?: number | null; moneda?: string | null }>();
    for (const raw of service.tickets ?? []) {
      if (raw?.id) {
        ticketsBy.set(String(raw.id), raw);
      }
    }

    const fechaAutomatica = this.calcAutoDate(advanceDays);
    return ticketIds
      .map((ticketId) => {
        const raw = ticketsBy.get(String(ticketId)) ?? {};
        return {
          ticketId: String(ticketId),
          nombreServicio: service.nombreServicio,
          serviceDay: service.serviceDay,
          fecha: service.fecha,
          total: Number(raw?.total) || 0,
          moneda: this.normalizeTicketMoneda(raw?.moneda),
          fechaAutomatica,
          fechaManual: null,
          status: 'automatico' as const,
          codigoReserva: null,
          codigoManual: false
        };
      })
      .filter((t) => t.ticketId);
  }

  /** Convierte la moneda del ticket ('$', 'S/', 'PEN', 'USD'…) a MassOsCurrency. */
  private normalizeTicketMoneda(moneda: string | null | undefined): MassOsCurrency {
    const value = String(moneda ?? '').toUpperCase().trim();
    if (value === 'D' || value === '$' || value === 'USD' || value.includes('DOLAR')) {
      return 'USD';
    }
    return 'PEN';
  }


  private calcAutoDate(advanceDays: number): Date {
    const result = new Date();
    result.setDate(result.getDate() + advanceDays);
    return result;
  }

  private resolveStatus(
    state: MassOsItineraryPaymentState,
    svc: MassOsServicePaymentState
  ): MassOsPaymentDateStatus {
    if (svc.fechaManual) return 'modificado';
    if (state.fechaMasiva) return 'cambio-masivo';
    return 'automatico';
  }

  private resolveTicketStatus(
    state: MassOsItineraryPaymentState,
    ticket: MassOsTicketPaymentState
  ): MassOsPaymentDateStatus {
    if (ticket.fechaManual) return 'modificado';
    if (state.fechaMasiva) return 'cambio-masivo';
    return 'automatico';
  }
}
